import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { SanctionBadge } from "@/components/SanctionBadge";
import {
  Upload,
  Film,
  Loader2,
  AlertCircle,
  Gauge,
  Scale,
  BookOpen,
  Wrench,
  Trash2,
  FileDown,
  ListChecks,
  Layers,
  Car,
  Lock,
  CreditCard,
  ScrollText,
  MapPin,
  Flag,
} from "lucide-react";

const MAX_MB = 80;

function useVideoSlot() {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const onSelect = (f, onError) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) {
      onError("Il file deve essere un video (mp4, mov, webm).");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      onError(`Il video supera il limite di ${MAX_MB}MB.`);
      return;
    }
    setFile(f);
  };

  const reset = () => {
    setFile(null);
    if (ref.current) ref.current.value = "";
  };

  return { file, url, ref, onSelect, reset, setFile };
}

function VideoSlot({ label, hint, slotKey, slot, setError, testidPrefix }) {
  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) slot.onSelect(e.dataTransfer.files[0], setError);
  };
  return (
    <div className="card" data-testid={`${testidPrefix}-card`}>
      <div className="flex items-center justify-between mb-2">
        <div className="section-h !mb-0">{label}</div>
        {slotKey === "secondary" && (
          <span className="font-mono-tel text-[10px] text-zinc-500 uppercase tracking-widest">
            opzionale
          </span>
        )}
      </div>
      {hint && <div className="text-xs text-zinc-500 mb-3 -mt-1">{hint}</div>}
      <input
        ref={slot.ref}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => slot.onSelect(e.target.files?.[0], setError)}
        data-testid={`${testidPrefix}-input`}
      />
      {!slot.file ? (
        <div
          onClick={() => slot.ref.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed ${
            dragOver ? "border-yellow-500 bg-yellow-500/5" : "border-zinc-700 bg-zinc-900/40"
          } hover:border-yellow-500 hover:bg-yellow-500/5 transition-all cursor-pointer flex flex-col items-center justify-center p-8 text-center`}
          data-testid={`${testidPrefix}-dropzone`}
        >
          <Upload className="w-8 h-8 text-yellow-500 mb-2" />
          <div className="font-display font-bold uppercase text-white text-sm tracking-wide">
            Trascina o clicca
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 font-mono-tel uppercase">
            MP4 · MOV · WEBM · max {MAX_MB}MB
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <video
            src={slot.url}
            controls
            className="w-full max-h-48 object-contain bg-black border border-zinc-800"
            data-testid={`${testidPrefix}-preview`}
          />
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Film className="w-3.5 h-3.5 text-yellow-500 flex-none" />
              <span className="font-mono-tel text-[11px] text-zinc-400 truncate">
                {slot.file.name}
              </span>
            </div>
            <button
              onClick={slot.reset}
              className="btn-danger text-xs"
              data-testid={`${testidPrefix}-remove`}
            >
              <Trash2 className="w-3 h-3" /> Rimuovi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const primary = useVideoSlot();
  const secondary = useVideoSlot();
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verdict, setVerdict] = useState(null);
  const [rulesCount, setRulesCount] = useState(0);

  useEffect(() => {
    api.get("/league-rules").then(({ data }) => setRulesCount(data.length)).catch(() => {});
  }, []);

  const isAdmin = user?.role === "admin";
  const subStatus = user?.subscription_status;
  const trialRemaining = user?.free_uses_remaining ?? 0;
  const accessBlocked = !isAdmin && subStatus !== "active" && trialRemaining <= 0;

  const onAnalyze = async () => {
    if (!primary.file) return;
    setLoading(true);
    setError("");
    setVerdict(null);
    try {
      const form = new FormData();
      form.append("video", primary.file);
      if (secondary.file) form.append("video_secondary", secondary.file);
      form.append("notes", notes);
      form.append("apply_league_rules", "true");
      const { data } = await api.post("/verdict/analyze", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
      });
      setVerdict(data);
      await refresh();
    } catch (err) {
      const code = err.response?.status;
      if (code === 402) {
        await refresh();
        setError(formatApiError(err.response?.data?.detail) || "Trial esaurito");
      } else {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const onResetAll = () => {
    primary.reset();
    secondary.reset();
    setVerdict(null);
    setNotes("");
    setError("");
  };

  const onExportPdf = async () => {
    if (!verdict) return;
    try {
      const res = await api.get(`/verdicts/${verdict.id}/pdf`, { responseType: "blob" });
      downloadBlob(res.data, `verdict_${verdict.id.slice(0, 8)}.pdf`);
    } catch {
      setError("Impossibile esportare il PDF.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-2">[ Analisi Incidente ]</div>
          <h1 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
            Console <span className="text-yellow-500">Verdetti</span>
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Carica fino a <span className="text-yellow-500">due clip</span> dello stesso incidente (POV principale +
            contro-video). L'IA confronta le prospettive e produce un verdetto tecnico secondo il Codice Sportivo FIA.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 text-zinc-500 font-mono-tel text-xs uppercase">
            <ListChecks className="w-4 h-4 text-yellow-500" />
            <span data-testid="rules-count">{rulesCount} regole lega configurate</span>
          </div>
          {!isAdmin && subStatus === "trial" && trialRemaining > 0 && (
            <Link
              to="/billing"
              className="text-xs font-mono-tel uppercase tracking-wider border border-yellow-500/40 bg-yellow-500/10 text-yellow-400 px-2 py-1 hover:bg-yellow-500/20"
              data-testid="trial-banner"
            >
              Trial · {trialRemaining} verdetto gratuito rimasto
            </Link>
          )}
        </div>
      </div>

      {accessBlocked && (
        <div className="border border-yellow-500/40 bg-yellow-500/5 p-5 md:p-6 mb-6 flex items-start gap-4" data-testid="paywall-banner">
          <Lock className="w-6 h-6 text-yellow-500 flex-none mt-1" />
          <div className="flex-1">
            <div className="font-display font-black uppercase text-xl">
              {subStatus === "expired" ? "Trial gratuito esaurito" : "Abbonamento richiesto"}
            </div>
            <p className="text-zinc-400 text-sm mt-1">
              Attiva l'abbonamento mensile a €15 per emettere verdetti illimitati.
            </p>
          </div>
          <button onClick={() => navigate("/billing")} className="btn-primary" data-testid="upgrade-button">
            <CreditCard className="w-4 h-4" /> Sottoscrivi
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Upload Column */}
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <VideoSlot
              label="1. Video Principale"
              hint="POV o replay principale"
              slotKey="primary"
              slot={primary}
              setError={setError}
              testidPrefix="video-primary"
            />
            <VideoSlot
              label="2. Contro-Video"
              hint="On-board avversario, replay alternativo"
              slotKey="secondary"
              slot={secondary}
              setError={setError}
              testidPrefix="video-secondary"
            />
          </div>

          {secondary.file && (
            <div className="flex items-center gap-2 text-xs text-yellow-500/90 font-mono-tel uppercase tracking-wider -mt-2">
              <Layers className="w-3.5 h-3.5" />
              Doppia sorgente attiva — l'IA confronterà entrambe le prospettive
            </div>
          )}

          <div className="card">
            <div className="section-h">3. Note del Segnalatore (opzionale)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Es. Curva 1, giro 12, contatto tra Auto A (rossa) e Auto B (blu)…"
              className="input-field"
              data-testid="notes-textarea"
            />
            <div className="mt-3 flex items-center gap-2 text-xs text-zinc-400 font-mono-tel uppercase tracking-wider">
              <ListChecks className="w-3.5 h-3.5 text-yellow-500" />
              {rulesCount > 0 ? (
                <span>
                  <span className="text-yellow-500">{rulesCount}</span> regole della lega saranno applicate automaticamente
                </span>
              ) : (
                <span>
                  Nessuna regola della lega configurata —{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/rules")}
                    className="text-yellow-500 underline"
                    data-testid="configure-rules-link"
                  >
                    configura
                  </button>
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onAnalyze}
              disabled={!primary.file || loading || accessBlocked}
              className="btn-primary flex-1"
              data-testid="analyze-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gauge className="w-4 h-4" />}
              {loading ? "Analisi in corso…" : accessBlocked ? "Abbonamento richiesto" : "Emetti Verdetto"}
            </button>
            {(primary.file || secondary.file || verdict) && !loading && (
              <button onClick={onResetAll} className="btn-secondary" data-testid="reset-all-button">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {error && (
            <div
              className="border border-red-500/40 bg-red-500/10 text-red-400 p-3 flex items-start gap-2"
              data-testid="dashboard-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-none" />
              <span className="text-sm">{error}</span>
            </div>
          )}
        </div>

        {/* Verdict Column */}
        <div className="lg:col-span-3">
          {!verdict && !loading && <EmptyVerdict />}
          {loading && <LoadingVerdict dual={!!secondary.file} />}
          {verdict && !loading && <VerdictPanel verdict={verdict} onExportPdf={onExportPdf} />}
        </div>
      </div>
    </div>
  );
}

function EmptyVerdict() {
  return (
    <div className="border border-zinc-800 bg-zinc-950 h-full min-h-[420px] flex items-center justify-center grid-bg">
      <div className="text-center px-6">
        <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-3">[ Standby ]</div>
        <h3 className="font-display font-black uppercase text-3xl text-zinc-300">In attesa di telemetria</h3>
        <p className="text-zinc-500 max-w-md mt-3">
          Carica un video (e opzionalmente un contro-video) e premi{" "}
          <span className="text-yellow-500">"Emetti Verdetto"</span>. Il commissario virtuale analizzerà i fotogrammi
          chiave del contatto.
        </p>
      </div>
    </div>
  );
}

function LoadingVerdict({ dual }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950 h-full min-h-[420px] flex flex-col items-center justify-center">
      <Loader2 className="w-10 h-10 text-yellow-500 animate-spin" />
      <div className="font-display font-black uppercase text-2xl mt-4">Analisi in corso</div>
      <div className="font-mono-tel text-xs text-zinc-500 uppercase mt-2 tracking-widest text-center px-6">
        {dual ? "Sincronizzazione doppia sorgente · " : ""}Estrazione fotogrammi · Valutazione FIA · Generazione verdetto
      </div>
    </div>
  );
}

export function VerdictPanel({ verdict, onExportPdf, onDelete }) {
  return (
    <div className="space-y-4" data-testid="verdict-panel">
      <div className="border border-yellow-500/40 bg-zinc-950 p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-2 flex items-center gap-2">
              [ Verdetto Ufficiale ]
              {verdict.dual_source && (
                <span className="inline-flex items-center gap-1 border border-yellow-500/40 text-yellow-400 px-1.5 py-0.5 text-[10px]">
                  <Layers className="w-3 h-3" /> Doppia sorgente
                </span>
              )}
            </div>
            <h2 className="font-display font-black uppercase text-2xl md:text-3xl leading-tight" data-testid="verdict-title">
              {verdict.title}
            </h2>
            {(verdict.circuito || verdict.giro) && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="verdict-context">
                {verdict.circuito && (
                  <span
                    className="inline-flex items-center gap-1.5 border border-yellow-500/40 bg-yellow-500/10 px-2.5 py-1 font-mono-tel text-[11px] uppercase tracking-wider text-yellow-300"
                    data-testid="verdict-circuito"
                  >
                    <MapPin className="w-3 h-3" /> {verdict.circuito}
                  </span>
                )}
                {verdict.giro && (
                  <span
                    className="inline-flex items-center gap-1.5 border border-yellow-500/40 bg-yellow-500/10 px-2.5 py-1 font-mono-tel text-[11px] uppercase tracking-wider text-yellow-300"
                    data-testid="verdict-giro"
                  >
                    <Flag className="w-3 h-3" /> {verdict.giro}
                  </span>
                )}
              </div>
            )}
            <div className="text-xs text-zinc-500 font-mono-tel mt-2">
              ID {verdict.id?.slice(0, 8)} · {new Date(verdict.created_at).toLocaleString()}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <SanctionBadge value={verdict.sanzione} size="lg" />
            <div className="flex items-center gap-2">
              {onExportPdf && (
                <button onClick={onExportPdf} className="btn-secondary text-xs" data-testid="export-pdf-button">
                  <FileDown className="w-3.5 h-3.5" /> Export PDF
                </button>
              )}
              {onDelete && (
                <button onClick={onDelete} className="btn-danger text-xs" data-testid="delete-verdict-button">
                  <Trash2 className="w-3.5 h-3.5" /> Elimina
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {Array.isArray(verdict.vetture) && verdict.vetture.length > 0 && (
        <div className="card" data-testid="card-vetture">
          <div className="flex items-center gap-2 mb-3">
            <Car className="w-4 h-4 text-yellow-500" />
            <div className="font-display font-black uppercase text-sm tracking-[0.18em] text-zinc-200">
              Vetture Coinvolte
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {verdict.vetture.map((v, i) => (
              <div
                key={i}
                className="border border-zinc-800 bg-zinc-900/50 p-3 flex items-start gap-3"
                data-testid={`vettura-${v.label || i}`}
              >
                <div className="w-9 h-9 bg-yellow-500 text-black font-display font-black flex items-center justify-center text-lg">
                  {v.label || String.fromCharCode(65 + i)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold uppercase text-white truncate">
                    {v.pilota || "Pilota non identificato"}
                    {v.numero && (
                      <span className="ml-2 font-mono-tel text-xs text-yellow-500">#{v.numero}</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5 font-mono-tel uppercase tracking-wider truncate">
                    {[v.team, v.modello].filter(Boolean).join(" · ") || "Team / modello incerto"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card icon={Gauge} title="Analisi della Dinamica" testid="card-dinamica">
          {verdict.dinamica}
        </Card>
        <Card icon={Scale} title="Responsabilità" testid="card-responsabilita">
          {verdict.responsabilita}
        </Card>
        <Card icon={BookOpen} title="Regola Violata (FIA)" testid="card-regola">
          {verdict.regola_violata}
        </Card>
        {verdict.regola_lega_applicata && !/^nessuna/i.test(verdict.regola_lega_applicata.trim()) && (
          <Card icon={ScrollText} title="Regola della Lega Applicata" testid="card-regola-lega" accent>
            {verdict.regola_lega_applicata}
          </Card>
        )}
        <Card icon={Wrench} title="Nota Tecnica" testid="card-nota" wide>
          {verdict.nota_tecnica}
        </Card>
      </div>

      {verdict.notes && (
        <div className="card">
          <div className="section-h">Note del Segnalatore</div>
          <p className="text-zinc-300">{verdict.notes}</p>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, title, children, testid, accent, wide }) {
  return (
    <div
      className={`card card-hover ${accent ? "border-yellow-500/50" : ""} ${wide ? "md:col-span-2" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${accent ? "text-yellow-400" : "text-yellow-500"}`} />
        <div className="font-display font-black uppercase text-sm tracking-[0.18em] text-zinc-200">{title}</div>
      </div>
      <p className="text-zinc-300 leading-relaxed text-[15px]">{children}</p>
    </div>
  );
}
